import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../AuthProvider';
import FetchTradeJobs from './service/FetchTradeJobs';
import MyJobsView from './views/MyJobsView';
import OverdueAtRiskView from './views/OverdueAtRiskView';
import TradeJobFormView from './views/TradeJobFormView';

const CORE_STATUSES = ['Initial', 'Inspection', 'Quote', 'Invoice', 'Completed', 'Incomplete'];
const STATUS_FILTER_OPTIONS = ['All', ...CORE_STATUSES, 'other'];
const CHART_STATUSES = [...CORE_STATUSES, 'Other'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_COLOR = {
  Initial: 'default',
  Inspection: 'processing',
  Quote: 'blue',
  Invoice: 'gold',
  Completed: 'success',
  Incomplete: 'error',
};

const STATUS_BAR_COLOR = {
  Initial: '#8c8c8c',
  Inspection: '#1677ff',
  Quote: '#2f54eb',
  Invoice: '#d48806',
  Completed: '#389e0d',
  Incomplete: '#cf1322',
  Other: '#722ed1',
};

const formatDateForView = (value) => {
  if (!value) return '-';

  // Already in DD-MM-YYYY format
  if (typeof value === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
    return value;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const dd = `${date.getDate()}`.padStart(2, '0');
  const mm = `${date.getMonth() + 1}`.padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatTimeForView = (job) => {
  if (Array.isArray(job.slots) && job.slots.length > 0) {
    return job.slots.join(', ');
  }

  if (job.starttime) return job.starttime;
  if (job.time) return job.time;

  return '-';
};

const parseJobDate = (job) => {
  const raw = job?.selectedDateString || job?.date;
  if (!raw) return null;

  if (typeof raw === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    const [dd, mm, yyyy] = raw.split('-');
    const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getFinancialYearStart = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return month >= 6 ? year : year - 1;
};

const getMonthKey = (year, monthIndex) => `${year}-${`${monthIndex + 1}`.padStart(2, '0')}`;

export default function TradeJobDashboard({ locationId }) {
  const auth = useAuth();
  const currentUserId = auth?.additionalUserInfo?.id;
  const [jobs, setJobs] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedChartMonth, setSelectedChartMonth] = useState('');
  const [selectedFyStart, setSelectedFyStart] = useState(getFinancialYearStart(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);

  const fetchJobs = useCallback(async () => {
    if (!auth?.token) return;

    setLoading(true);
    setError('');

    try {
      const tradeJobs = await FetchTradeJobs(auth.token);
      const withKeys = tradeJobs.map((job) => ({ ...job, key: job.id || uuidv4() }));

      setJobs(withKeys);
    } catch (err) {
      console.error('Failed to load trade jobs:', err);
      setError('Could not load trade jobs. Please check API endpoint and permissions.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const locationFilteredJobs = useMemo(() => {
    if (!locationId) return jobs;
    return jobs.filter((job) => job.locationId === locationId);
  }, [jobs, locationId]);

  const financialYearStart = useMemo(() => getFinancialYearStart(new Date()), []);
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, idx) => {
      const monthIndex = (6 + idx) % 12;
      const year = monthIndex >= 6 ? financialYearStart : financialYearStart + 1;
      return {
        value: getMonthKey(year, monthIndex),
        label: `${MONTH_LABELS[monthIndex]} ${year}`,
      };
    });
  }, [financialYearStart]);

  useEffect(() => {
    if (selectedChartMonth) return;

    const now = new Date();
    const currentMonthKey = getMonthKey(now.getFullYear(), now.getMonth());
    const exists = monthOptions.some((item) => item.value === currentMonthKey);
    setSelectedChartMonth(exists ? currentMonthKey : monthOptions[0]?.value || '');
  }, [selectedChartMonth, monthOptions]);

  const rows = useMemo(() => {
    let filteredRows = locationFilteredJobs;

    if (selectedStatus === 'other') {
      filteredRows = locationFilteredJobs.filter((job) => !CORE_STATUSES.includes(job?.status));
    } else if (selectedStatus !== 'All') {
      filteredRows = locationFilteredJobs.filter((job) => job?.status === selectedStatus);
    }

    return [...filteredRows].sort((a, b) => {
      if (selectedStatus === 'All') {
        const aCompleted = a?.status === 'Completed';
        const bCompleted = b?.status === 'Completed';
        if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      }

      const aDate = parseJobDate(a);
      const bDate = parseJobDate(b);
      if (aDate && bDate) return bDate.getTime() - aDate.getTime();
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    });
  }, [locationFilteredJobs, selectedStatus]);

  const urgencyGroups = useMemo(() => {
    const grouped = {
      Today: [],
      Tomorrow: [],
      Upcoming: [],
      Old: [],
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    locationFilteredJobs.forEach((job) => {
      const parsedDate = parseJobDate(job);
      if (!parsedDate) {
        grouped.Old.push(job);
        return;
      }

      const dayOnly = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());

      if (dayOnly.getTime() === today.getTime()) {
        grouped.Today.push(job);
      } else if (dayOnly.getTime() === tomorrow.getTime()) {
        grouped.Tomorrow.push(job);
      } else if (dayOnly.getTime() > tomorrow.getTime()) {
        grouped.Upcoming.push(job);
      } else {
        grouped.Old.push(job);
      }
    });

    const sortByDateDesc = (a, b) => {
      const aDate = parseJobDate(a);
      const bDate = parseJobDate(b);
      if (aDate && bDate) return bDate.getTime() - aDate.getTime();
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    };

    return {
      Today: [...grouped.Today].sort(sortByDateDesc),
      Tomorrow: [...grouped.Tomorrow].sort(sortByDateDesc),
      Upcoming: [...grouped.Upcoming].sort(sortByDateDesc),
      Old: [...grouped.Old].sort(sortByDateDesc),
    };
  }, [locationFilteredJobs]);

  const chartData = useMemo(() => {
    if (!selectedChartMonth) return [];

    const [selectedYear, selectedMonth] = selectedChartMonth.split('-').map(Number);
    const statusCounts = {
      Initial: 0,
      Inspection: 0,
      Quote: 0,
      Invoice: 0,
      Completed: 0,
      Incomplete: 0,
      Other: 0,
    };

    locationFilteredJobs.forEach((job) => {
      const d = parseJobDate(job);
      if (!d) return;
      if (d.getFullYear() !== selectedYear || d.getMonth() + 1 !== selectedMonth) return;

      const jobStatus = CORE_STATUSES.find(
        status => status.toLowerCase() === job?.status?.toLowerCase()
      ) || 'Other';
      statusCounts[jobStatus] += 1;
    });

    return CHART_STATUSES.map((status) => ({
      status,
      count: statusCounts[status] || 0,
    }));
  }, [locationFilteredJobs, selectedChartMonth]);

  const fyOptions = useMemo(() => {
    const fySet = new Set([getFinancialYearStart(new Date())]);

    locationFilteredJobs.forEach((job) => {
      const d = parseJobDate(job);
      if (!d) return;
      fySet.add(getFinancialYearStart(d));
    });

    return [...fySet]
      .sort((a, b) => b - a)
      .map((fyStart) => ({
        value: fyStart,
        label: `Jul ${fyStart} - Jun ${fyStart + 1}`,
      }));
  }, [locationFilteredJobs]);

  useEffect(() => {
    if (fyOptions.some((opt) => opt.value === selectedFyStart)) return;
    if (fyOptions[0]) {
      setSelectedFyStart(fyOptions[0].value);
    }
  }, [fyOptions, selectedFyStart]);

  const fyChartData = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, idx) => {
      const monthIndex = (6 + idx) % 12;
      const year = monthIndex >= 6 ? selectedFyStart : selectedFyStart + 1;
      const key = getMonthKey(year, monthIndex);
      const counts = CHART_STATUSES.reduce((acc, status) => {
        acc[status] = 0;
        return acc;
      }, {});

      return {
        key,
        monthIndex,
        year,
        label: `${MONTH_LABELS[monthIndex]} ${year}`,
        shortLabel: MONTH_LABELS[monthIndex],
        counts,
      };
    });

    const monthMap = months.reduce((acc, m) => {
      acc[m.key] = m;
      return acc;
    }, {});

    locationFilteredJobs.forEach((job) => {
      const d = parseJobDate(job);
      if (!d) return;
      const fyStart = getFinancialYearStart(d);
      if (fyStart !== selectedFyStart) return;

      const key = getMonthKey(d.getFullYear(), d.getMonth());
      if (!monthMap[key]) return;

      const status = CORE_STATUSES.includes(job?.status) ? job.status : 'Other';
      monthMap[key].counts[status] += 1;
    });

    return months;
  }, [locationFilteredJobs, selectedFyStart]);

  const fyChartMax = useMemo(() => {
    const max = fyChartData.reduce((outerMax, month) => {
      const monthMax = CHART_STATUSES.reduce((innerMax, status) => {
        return Math.max(innerMax, month.counts[status] || 0);
      }, 0);
      return Math.max(outerMax, monthMax);
    }, 0);

    return Math.max(max, 1);
  }, [fyChartData]);

  const openCreateJob = () => {
    setEditingJob(null);
    setIsFormOpen(true);
  };

  const openEditJob = (job) => {
    setEditingJob(job);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingJob(null);
  };

  const handleSaved = () => {
    fetchJobs();
  };

  const columns = [
    {
      title: 'Job Title',
      dataIndex: 'jobName',
      key: 'jobName',
      render: (_, record) => record.jobName || record.title || '-',
    },
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (_, record) => record.customerName || '-',
    },
    {
      title: 'Date',
      dataIndex: 'selectedDateString',
      key: 'date',
      render: (_, record) => formatDateForView(record.selectedDateString || record.date),
    },
    {
      title: 'Time',
      key: 'time',
      render: (_, record) => formatTimeForView(record),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={STATUS_COLOR[status] || 'default'}>{status || 'Unknown'}</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button type="link" onClick={() => openEditJob(record)} style={{ paddingInline: 0 }}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        Trade Job Dashboard
      </Typography.Title>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

      <Tabs
        defaultActiveKey="chart-by-fy"
        items={[
          {
            key: 'chart-by-fy',
            label: 'Chart View (by FY)',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Typography.Text strong>
                    Financial Year Trend (Months on X-axis)
                  </Typography.Text>
                  <Space>
                    <Select
                      value={selectedFyStart}
                      onChange={setSelectedFyStart}
                      style={{ width: 220 }}
                      options={fyOptions}
                    />
                    <Button onClick={fetchJobs} loading={loading}>
                      Refresh
                    </Button>
                  </Space>
                </Space>

                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, overflowX: 'auto' }}>
                  <div style={{ minWidth: 980 }}>
                    <div style={{ height: 260, display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                      {fyChartData.map((monthItem) => (
                        <div
                          key={monthItem.key}
                          style={{
                            flex: 1,
                            minWidth: 60,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <div style={{ height: 210, width: '100%', display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                            {CHART_STATUSES.map((status) => {
                              const count = monthItem.counts[status] || 0;
                              const heightPct = (count / fyChartMax) * 100;
                              return (
                                <div
                                  key={`${monthItem.key}-${status}`}
                                  title={`${monthItem.label} • ${status}: ${count}`}
                                  style={{
                                    flex: 1,
                                    minHeight: count > 0 ? 4 : 0,
                                    height: `${heightPct}%`,
                                    background: STATUS_BAR_COLOR[status] || '#1677ff',
                                    borderRadius: '4px 4px 0 0',
                                  }}
                                />
                              );
                            })}
                          </div>
                          <Typography.Text style={{ fontSize: 12 }}>{monthItem.shortLabel}</Typography.Text>
                        </div>
                      ))}
                    </div>

                    <Space wrap size={[14, 8]} style={{ marginTop: 12 }}>
                      {CHART_STATUSES.map((status) => (
                        <Space key={status} size={6}>
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              display: 'inline-block',
                              borderRadius: 2,
                              background: STATUS_BAR_COLOR[status] || '#1677ff',
                            }}
                          />
                          <Typography.Text style={{ fontSize: 12 }}>{status}</Typography.Text>
                        </Space>
                      ))}
                    </Space>
                  </div>
                </div>
              </Space>
            ),
          },
          {
            key: 'simple',
            label: 'Simple View',
            children: (
              <>
                <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
                  <Button type="primary" onClick={openCreateJob}>
                    Create Job
                  </Button>
                  <Space>
                    <Select
                      value={selectedStatus}
                      onChange={setSelectedStatus}
                      style={{ width: 180 }}
                      options={STATUS_FILTER_OPTIONS.map((status) => ({
                        value: status,
                        label: status === 'other' ? 'Other' : status,
                      }))}
                    />
                    <Button onClick={fetchJobs} loading={loading}>
                      Refresh
                    </Button>
                  </Space>
                </Space>

                <Table
                  columns={columns}
                  dataSource={rows}
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 900 }}
                />
              </>
            ),
          },
          {
            key: 'urgency',
            label: 'Urgency View',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                {['Today', 'Tomorrow', 'Upcoming', 'Old'].map((groupName) => (
                  <div key={groupName}>
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      {groupName}
                    </Typography.Title>
                    <Table
                      columns={columns}
                      dataSource={urgencyGroups[groupName]}
                      loading={loading}
                      pagination={false}
                      scroll={{ x: 900 }}
                      locale={{ emptyText: `No ${groupName.toLowerCase()} items` }}
                    />
                  </div>
                ))}
              </Space>
            ),
          },
          {
            key: 'my-jobs',
            label: 'My Jobs',
            children: (
              <MyJobsView
                jobs={locationFilteredJobs}
                columns={columns}
                loading={loading}
                onRefresh={fetchJobs}
                currentUserId={currentUserId}
              />
            ),
          },
          {
            key: 'overdue-risk',
            label: 'Overdue / At Risk',
            children: (
              <OverdueAtRiskView
                jobs={locationFilteredJobs}
                columns={columns}
                loading={loading}
                onRefresh={fetchJobs}
              />
            ),
          },
          {
            key: 'chart',
            label: 'Chart View',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Typography.Text strong>
                    Financial Year: {`Jul ${financialYearStart} - Jun ${financialYearStart + 1}`}
                  </Typography.Text>
                  <Space>
                    <Select
                      value={selectedChartMonth}
                      onChange={setSelectedChartMonth}
                      style={{ width: 180 }}
                      options={monthOptions}
                    />
                    <Button onClick={fetchJobs} loading={loading}>
                      Refresh
                    </Button>
                  </Space>
                </Space>

                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
                  {chartData.map((item) => {
                    const maxCount = Math.max(...chartData.map((x) => x.count), 1);
                    const widthPercent = `${(item.count / maxCount) * 100}%`;

                    return (
                      <div
                        key={item.status}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '120px 1fr 40px',
                          alignItems: 'center',
                          gap: 12,
                          marginBottom: 10,
                        }}
                      >
                        <Typography.Text>{item.status}</Typography.Text>
                        <div
                          style={{
                            height: 22,
                            background: '#f5f5f5',
                            borderRadius: 6,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              width: item.count > 0 ? widthPercent : '0%',
                              background: STATUS_BAR_COLOR[item.status] || '#1677ff',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                        <Typography.Text strong>{item.count}</Typography.Text>
                      </div>
                    );
                  })}
                </div>
              </Space>
            ),
          },
        ]}
      />

      <TradeJobFormView
        open={isFormOpen}
        onClose={closeForm}
        onSaved={handleSaved}
        token={auth?.token}
        additionalUserInfo={auth?.additionalUserInfo}
        initialJob={editingJob}
        presetLocationId={locationId}
      />
    </div>
  );
}
